import React, { useState, useEffect } from 'react';
import { XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';

const VehicleForm = ({ vehicle, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    registration_number: '',
    type: '',
    capacity: '',
    model: '',
    model_year: '',
    description: '',
    features: ['Air Conditioning', '4x4', 'Roof Rack'],
    status: 'available',
    rate_per_day: '',
    rate_per_km: '',
    make: '',
    last_maintenance_date: '',
    next_maintenance_date: '',
    is_active: true,
    image: null
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newFeature, setNewFeature] = useState('');

  // Initialize form data when editing an existing vehicle
  useEffect(() => {
    if (vehicle) {
      console.log('Initializing form with vehicle data:', vehicle);
      
      // Process features to ensure it's an array
      let vehicleFeatures = [];
      if (vehicle.features) {
        if (typeof vehicle.features === 'string') {
          try {
            vehicleFeatures = JSON.parse(vehicle.features);
            console.log('Features parsed from JSON string:', vehicleFeatures);
          } catch (e) {
            console.error('Error parsing features:', e);
            vehicleFeatures = [];
          }
        } else if (Array.isArray(vehicle.features)) {
          vehicleFeatures = vehicle.features;
        }
      }
      
      console.log('Processed features for form:', vehicleFeatures);
      
      setFormData({
        name: vehicle.name || '',
        registration_number: vehicle.registration_number || '',
        type: vehicle.type || '',
        capacity: vehicle.capacity || '',
        model: vehicle.model || '',
        model_year: vehicle.model_year || '',
        description: vehicle.description || '',
        features: vehicleFeatures,
        status: vehicle.status || 'available',
        rate_per_day: vehicle.rate_per_day || '',
        rate_per_km: vehicle.rate_per_km || '',
        make: vehicle.make || '',
        last_maintenance_date: vehicle.last_maintenance_date || '',
        next_maintenance_date: vehicle.next_maintenance_date || '',
        is_active: vehicle.is_active !== undefined ? vehicle.is_active : true,
        image: null // We don't populate the file input
      });
      
      // Set image preview for existing vehicle
      if (vehicle.image) {
        setImagePreview(vehicle.image);
      }
    }
  }, [vehicle]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value, type } = e.target;
    
    if (type === 'file') {
      const file = e.target.files[0];
      setFormData(prev => ({ ...prev, [name]: file }));
      
      // Create preview URL for the selected image
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        setImagePreview(null);
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  // Add a new feature
  const addFeature = () => {
    if (newFeature.trim() === '') return;
    
    // Ensure features is an array
    const currentFeatures = Array.isArray(formData.features) ? formData.features : [];
    
    if (!currentFeatures.includes(newFeature.trim())) {
      console.log('Adding feature:', newFeature.trim());
      setFormData(prev => ({
        ...prev,
        features: [...currentFeatures, newFeature.trim()]
      }));
      setNewFeature('');
    }
  };

  // Remove a feature
  const removeFeature = (feature) => {
    console.log('Removing feature:', feature);
    
    // Ensure features is an array
    const currentFeatures = Array.isArray(formData.features) ? formData.features : [];
    
    setFormData(prev => ({
      ...prev,
      features: currentFeatures.filter(f => f !== feature)
    }));
  };

  // Handle feature input keypress
  const handleFeatureKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addFeature();
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Vehicle name is required';
    }
    
    if (!formData.registration_number.trim()) {
      newErrors.registration_number = 'Registration number is required';
    }
    
    if (!formData.type.trim()) {
      newErrors.type = 'Vehicle type is required';
    }
    
    if (!formData.model.trim()) {
      newErrors.model = 'Vehicle model is required';
    }
    
    if (!formData.model_year) {
      newErrors.model_year = 'Model year is required';
    } else if (
      isNaN(formData.model_year) || 
      parseInt(formData.model_year) < 1900 || 
      parseInt(formData.model_year) > new Date().getFullYear() + 1
    ) {
      newErrors.model_year = 'Please enter a valid model year';
    }
    
    if (!formData.capacity) {
      newErrors.capacity = 'Capacity is required';
    } else if (isNaN(formData.capacity) || parseInt(formData.capacity) <= 0) {
      newErrors.capacity = 'Capacity must be a positive number';
    }
    
    // Only require image for new vehicles
    if (!vehicle && !formData.image) {
      newErrors.image = 'Vehicle image is required';
    }
    
    setErrors(newErrors);
    return newErrors;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Prepare form data for submission
      const submitData = new FormData();
      
      // Log the form data being prepared
      console.log('Preparing vehicle data for submission:', formData);
      
      // Add all form fields to FormData
      Object.keys(formData).forEach(key => {
        if (key === 'features' && Array.isArray(formData[key])) {
          // Convert features array to JSON string
          submitData.append('features', JSON.stringify(formData[key]));
          console.log('Features prepared for submission:', JSON.stringify(formData[key]));
        } else if (key === 'is_active') {
          // Convert string 'true'/'false' to boolean for is_active
          submitData.append('is_active', formData[key] === true || formData[key] === 'true' ? 1 : 0);
        } else if (key === 'image') {
          // Only append image if it exists
          if (formData[key] !== null) {
            submitData.append('image', formData[key]);
          }
        } else {
          // For all other fields
          submitData.append(key, formData[key]);
        }
      });
      
      // If editing and no new image is selected, we need to tell the backend to keep the existing image
      if (vehicle && !formData.image) {
        submitData.append('keep_existing_image', 'true');
      }
      
      // Log the form data being sent
      console.log('Submitting vehicle data:', Object.fromEntries(submitData.entries()));
      console.log('Submission URL:', vehicle ? `/api/v1/admin/vehicles/${vehicle.id}` : '/api/v1/admin/vehicles');
      
      await onSubmit(submitData);
    } catch (error) {
      console.error('Error submitting form:', error);
      setErrors(prev => ({ ...prev, form: 'Failed to save vehicle. Please try again.' }));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Remove image
  const removeImage = () => {
    setFormData(prev => ({ ...prev, image: null }));
    setImagePreview(null);
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">
          {vehicle ? 'Edit Vehicle' : 'Add New Vehicle'}
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          {vehicle 
            ? 'Update the details of the existing vehicle' 
            : 'Fill in the details to add a new vehicle to the fleet'}
        </p>
      </div>

      {errors.form && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-600">{errors.form}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
          {/* Vehicle Name */}
          <div className="sm:col-span-3">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
              Vehicle Name *
            </label>
            <div className="mt-1">
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md ${
                  errors.name ? 'border-red-300' : ''
                }`}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name}</p>
              )}
            </div>
          </div>

          {/* Registration Number */}
          <div className="sm:col-span-3">
            <label htmlFor="registration_number" className="block text-sm font-medium text-gray-700">
              Registration Number *
            </label>
            <div className="mt-1">
              <input
                type="text"
                id="registration_number"
                name="registration_number"
                value={formData.registration_number}
                onChange={handleChange}
                className={`shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md ${
                  errors.registration_number ? 'border-red-300' : ''
                }`}
              />
              {errors.registration_number && (
                <p className="mt-1 text-sm text-red-600">{errors.registration_number}</p>
              )}
            </div>
          </div>

          {/* Vehicle Type */}
          <div className="sm:col-span-2">
            <label htmlFor="type" className="block text-sm font-medium text-gray-700">
              Vehicle Type *
            </label>
            <div className="mt-1">
              <select
                id="type"
                name="type"
                value={formData.type}
                onChange={handleChange}
                className={`shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md ${
                  errors.type ? 'border-red-300' : ''
                }`}
              >
                <option value="">Select Type</option>
                <option value="SUV">SUV</option>
                <option value="Van">Van</option>
                <option value="Minibus">Minibus</option>
                <option value="Bus">Bus</option>
                <option value="Sedan">Sedan</option>
              </select>
              {errors.type && (
                <p className="mt-1 text-sm text-red-600">{errors.type}</p>
              )}
            </div>
          </div>

          {/* Model */}
          <div className="sm:col-span-2">
            <label htmlFor="model" className="block text-sm font-medium text-gray-700">
              Model *
            </label>
            <div className="mt-1">
              <input
                type="text"
                id="model"
                name="model"
                value={formData.model}
                onChange={handleChange}
                className={`shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md ${
                  errors.model ? 'border-red-300' : ''
                }`}
              />
              {errors.model && (
                <p className="mt-1 text-sm text-red-600">{errors.model}</p>
              )}
            </div>
          </div>

          {/* Model Year */}
          <div className="sm:col-span-2">
            <label htmlFor="model_year" className="block text-sm font-medium text-gray-700">
              Model Year *
            </label>
            <div className="mt-1">
              <input
                type="number"
                id="model_year"
                name="model_year"
                value={formData.model_year}
                onChange={handleChange}
                className={`shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md ${
                  errors.model_year ? 'border-red-300' : ''
                }`}
              />
              {errors.model_year && (
                <p className="mt-1 text-sm text-red-600">{errors.model_year}</p>
              )}
            </div>
          </div>

          {/* Capacity */}
          <div className="sm:col-span-2">
            <label htmlFor="capacity" className="block text-sm font-medium text-gray-700">
              Capacity (persons) *
            </label>
            <div className="mt-1">
              <input
                type="number"
                id="capacity"
                name="capacity"
                min="1"
                value={formData.capacity}
                onChange={handleChange}
                className={`shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md ${
                  errors.capacity ? 'border-red-300' : ''
                }`}
              />
              {errors.capacity && (
                <p className="mt-1 text-sm text-red-600">{errors.capacity}</p>
              )}
            </div>
          </div>

          {/* Status */}
          <div className="sm:col-span-3">
            <label htmlFor="status" className="block text-sm font-medium text-gray-700">
              Status *
            </label>
            <div className="mt-1">
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                className={`shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md ${
                  errors.status ? 'border-red-300' : ''
                }`}
              >
                <option value="available">Available</option>
                <option value="unavailable">Unavailable</option>
              </select>
              {errors.status && (
                <p className="mt-1 text-sm text-red-600">{errors.status}</p>
              )}
            </div>
          </div>

          {/* Rate per day */}
          <div className="sm:col-span-2">
            <label htmlFor="rate_per_day" className="block text-sm font-medium text-gray-700">
              Rate per day
            </label>
            <div className="mt-1">
              <input
                type="number"
                id="rate_per_day"
                name="rate_per_day"
                value={formData.rate_per_day}
                onChange={handleChange}
                className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
              />
            </div>
          </div>

          {/* Rate per km */}
          <div className="sm:col-span-2">
            <label htmlFor="rate_per_km" className="block text-sm font-medium text-gray-700">
              Rate per km
            </label>
            <div className="mt-1">
              <input
                type="number"
                id="rate_per_km"
                name="rate_per_km"
                value={formData.rate_per_km}
                onChange={handleChange}
                className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
              />
            </div>
          </div>

          {/* Make */}
          <div className="sm:col-span-2">
            <label htmlFor="make" className="block text-sm font-medium text-gray-700">
              Make
            </label>
            <div className="mt-1">
              <input
                type="text"
                id="make"
                name="make"
                value={formData.make}
                onChange={handleChange}
                className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
              />
            </div>
          </div>

          {/* Last Maintenance Date */}
          <div className="sm:col-span-2">
            <label htmlFor="last_maintenance_date" className="block text-sm font-medium text-gray-700">
              Last Maintenance Date
            </label>
            <div className="mt-1">
              <input
                type="date"
                id="last_maintenance_date"
                name="last_maintenance_date"
                value={formData.last_maintenance_date}
                onChange={handleChange}
                className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
              />
            </div>
          </div>

          {/* Next Maintenance Date */}
          <div className="sm:col-span-2">
            <label htmlFor="next_maintenance_date" className="block text-sm font-medium text-gray-700">
              Next Maintenance Date
            </label>
            <div className="mt-1">
              <input
                type="date"
                id="next_maintenance_date"
                name="next_maintenance_date"
                value={formData.next_maintenance_date}
                onChange={handleChange}
                className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
              />
            </div>
          </div>

          {/* Is Active */}
          <div className="sm:col-span-2">
            <label htmlFor="is_active" className="block text-sm font-medium text-gray-700">
              Is Active
            </label>
            <div className="mt-1">
              <select
                id="is_active"
                name="is_active"
                value={formData.is_active}
                onChange={handleChange}
                className={`shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md ${
                  errors.is_active ? 'border-red-300' : ''
                }`}
              >
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
              {errors.is_active && (
                <p className="mt-1 text-sm text-red-600">{errors.is_active}</p>
              )}
            </div>
          </div>

          {/* Vehicle Image */}
          <div className="sm:col-span-6">
            <label htmlFor="image" className="block text-sm font-medium text-gray-700">
              Vehicle Image {!vehicle && '*'}
            </label>
            <div className="mt-1 flex items-center">
              {imagePreview ? (
                <div className="relative">
                  <img
                    src={imagePreview}
                    alt="Vehicle preview"
                    className="h-32 w-auto object-cover rounded-md"
                  />
                  <button
                    type="button"
                    onClick={removeImage}
                    className="absolute top-0 right-0 -mt-2 -mr-2 bg-red-100 rounded-full p-1 text-red-600 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    <span className="sr-only">Remove</span>
                    <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>
              ) : (
                <div className="flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md w-full">
                  <div className="space-y-1 text-center">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 48 48"
                      aria-hidden="true"
                    >
                      <path
                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div className="flex text-sm text-gray-600">
                      <label
                        htmlFor="image"
                        className="relative cursor-pointer bg-white rounded-md font-medium text-primary hover:text-primary-dark focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary"
                      >
                        <span>Upload a file</span>
                        <input
                          id="image"
                          name="image"
                          type="file"
                          accept="image/*"
                          onChange={handleChange}
                          className="sr-only"
                        />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB</p>
                  </div>
                </div>
              )}
            </div>
            {errors.image && (
              <p className="mt-1 text-sm text-red-600">{errors.image}</p>
            )}
          </div>

          {/* Description */}
          <div className="sm:col-span-6">
            <label htmlFor="description" className="block text-sm font-medium text-gray-700">
              Description
            </label>
            <div className="mt-1">
              <textarea
                id="description"
                name="description"
                rows={3}
                value={formData.description}
                onChange={handleChange}
                className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
              />
            </div>
            <p className="mt-2 text-sm text-gray-500">
              Brief description of the vehicle and its features.
            </p>
          </div>

          {/* Features */}
          <div className="sm:col-span-6">
            <label htmlFor="features" className="block text-sm font-medium text-gray-700">
              Features
            </label>
            <div className="mt-1">
              <div className="flex flex-wrap gap-2 mb-3">
                {formData.features.map((feature, index) => (
                  <div 
                    key={index} 
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                  >
                    {feature}
                    <button
                      type="button"
                      onClick={() => removeFeature(feature)}
                      className="ml-1.5 h-4 w-4 rounded-full inline-flex items-center justify-center text-blue-600 hover:bg-blue-200 hover:text-blue-900"
                    >
                      <XMarkIcon className="h-3 w-3" />
                      <span className="sr-only">Remove {feature}</span>
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex">
                <input
                  type="text"
                  id="new-feature"
                  value={newFeature}
                  onChange={(e) => setNewFeature(e.target.value)}
                  onKeyPress={handleFeatureKeyPress}
                  placeholder="Add a feature"
                  className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md rounded-r-none"
                />
                <button
                  type="button"
                  onClick={addFeature}
                  className="inline-flex items-center px-3 py-2 border border-l-0 border-gray-300 shadow-sm text-sm font-medium rounded-r-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  <PlusIcon className="h-4 w-4" />
                  <span className="sr-only">Add feature</span>
                </button>
              </div>
            </div>
            <p className="mt-2 text-sm text-gray-500">
              Add features of the vehicle like Air Conditioning, 4x4, Roof Rack, etc.
            </p>
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-5">
          <button
            type="button"
            onClick={onCancel}
            className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
          >
            {isSubmitting ? 'Saving...' : vehicle ? 'Update Vehicle' : 'Add Vehicle'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default VehicleForm;
